import { useState } from "react";
import { Link } from "react-router-dom";
import { siteData } from "@/store/siteData";
import useLogoUrl from "@/hooks/useLogoUrl";
import type { UserRole } from "@/utils/userStore";
import { getFeatureVisibility, getFeatureDescription, isFeatureEnabled, isPremiumTrialActive, getPremiumTrialInfo } from "@/pages/dev-panel/page";
import InspectorPremiumPopup from "@/components/feature/InspectorPremiumPopup";
import PremiumLockedButton, { PREMIUM_POPUP_CONFIGS } from "@/components/feature/PremiumLockedButton";

export type Tab = "dashboard" | "homepage" | "about" | "projects" | "partners" | "messages" | "livesupport" | "settings" | "changelog" | "users" | "ads" | "navigation";

interface AdminSidebarProps {
  activeTab: Tab;
  onTabChange: (tab: Tab) => void;
  newMessages: number;
  supportUnread?: number;
  userRole?: UserRole;
}

const sectionKeyMap: Record<Tab, string> = {
  dashboard: "", homepage: "yg_hero", about: "yg_about", projects: "yg_projects",
  partners: "yg_partners", messages: "", livesupport: "", settings: "", changelog: "", users: "",
};

// Feature ID map — her tab için hangi feature ID'si kontrol edilecek
const tabFeatureMap: Partial<Record<Tab, string>> = {
  livesupport: "livesupport",
  changelog:   "changelog",
  ads:         "ad_manager",
};



const allNavItems: { id: Tab; label: string; icon: string; roles?: UserRole[] }[] = [
  { id: "dashboard",   label: "Genel Bakış",         icon: "ri-dashboard-3-line",        roles: ["admin", "editor"] },
  { id: "homepage",    label: "Ana Sayfa",            icon: "ri-home-4-line",             roles: ["admin", "editor"] },
  { id: "about",       label: "Hakkımızda",          icon: "ri-information-line",        roles: ["admin", "editor"] },
  { id: "projects",    label: "Projeler",             icon: "ri-building-2-line",         roles: ["admin", "editor"] },
  { id: "partners",    label: "Stratejik Ortaklar",   icon: "ri-links-line",              roles: ["admin", "editor"] },
  { id: "navigation",  label: "Sayfa Yönetimi",       icon: "ri-pages-line",              roles: ["admin"] },
  { id: "messages",    label: "Mesajlar",             icon: "ri-mail-line" },
  { id: "livesupport", label: "Destek Merkezi",       icon: "ri-customer-service-2-line" },
  { id: "ads",         label: "Reklam Yönetimi",      icon: "ri-advertisement-line",      roles: ["admin", "editor"] },
  { id: "settings",    label: "Site Ayarları",        icon: "ri-settings-3-line",         roles: ["admin", "editor"] },
  { id: "users",       label: "Kullanıcılar",         icon: "ri-shield-user-line",        roles: ["admin"] },
];

function formatRelativeTime(iso: string): string {
  try {
    const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (diff < 60) return "az önce";
    if (diff < 3600) return `${Math.floor(diff / 60)}dk`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}sa`;
    return `${Math.floor(diff / 86400)}g`;
  } catch { return ""; }
}

// ── Inspector Kilitli Sidebar Butonu (zengin hover popup ile) ───────────────
function InspectorLockedSidebarButton() {
  const [showPopup, setShowPopup] = useState(false);
  return (
    <div
      className="relative w-full"
      onMouseEnter={() => setShowPopup(true)}
      onMouseLeave={() => setShowPopup(false)}
    >
      <div className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border border-amber-500/20 bg-amber-500/5 cursor-not-allowed select-none">
        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
          <i className="ri-cursor-line text-sm text-amber-400/70"></i>
        </div>
        <span className="flex-1 text-xs text-amber-300/80">Görsel İnspektör</span>
        <div className="flex items-center gap-1">
          <div className="w-3.5 h-3.5 flex items-center justify-center">
            <i className="ri-lock-fill text-amber-500 text-xs"></i>
          </div>
          <span className="text-amber-500/60 text-[9px] font-semibold uppercase tracking-wide">Premium</span>
        </div>
      </div>
      <InspectorPremiumPopup visible={showPopup} />
    </div>
  );
}

// ── Tooltip bileşeni ──────────────────────────────────────────────────────────
function LockTooltip({ text }: { text: string }) {
  return (
    <div className="absolute left-full top-1/2 -translate-y-1/2 ml-3 z-[9999] pointer-events-none">
      <div className="bg-[#1A2744] border border-amber-500/30 rounded-lg px-3 py-2.5 shadow-2xl" style={{ width: "256px" }}>
        <div className="flex items-start gap-2">
          <i className="ri-lock-fill text-amber-400 text-sm flex-shrink-0 mt-0.5"></i>
          <div>
            <p className="text-amber-300 text-xs font-semibold mb-1">Kilitli Özellik</p>
            <p className="text-[#C8D4E8] text-xs leading-relaxed">{text}</p>
            <p className="text-amber-400/80 text-xs mt-1.5 font-medium border-t border-amber-500/20 pt-1.5">
              Kilitli özellikler için geliştiriciniz ile iletişime geçin.
            </p>
          </div>
        </div>
        <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-[#1A2744]"></div>
      </div>
    </div>
  );
}

// ── Kilitli nav butonu ────────────────────────────────────────────────────────
function LockedNavButton({ label, icon, description }: { label: string; icon: string; description: string }) {
  const [showTip, setShowTip] = useState(false);
  return (
    <div
      className="relative w-full"
      onMouseEnter={() => setShowTip(true)}
      onMouseLeave={() => setShowTip(false)}
    >
      <div className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium border border-transparent opacity-40 cursor-not-allowed select-none">
        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
          <i className={`${icon} text-base text-[#7B8FAF]`}></i>
        </div>
        <span className="flex-1 truncate text-[#7B8FAF]">{label}</span>
        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
          <i className="ri-lock-fill text-amber-500/70 text-xs"></i>
        </div>
      </div>
      {showTip && <LockTooltip text={description} />}
    </div>
  );
}

export default function AdminSidebar({ activeTab, onTabChange, newMessages, supportUnread = 0, userRole = "editor" }: AdminSidebarProps) {
  const lastChanged = siteData.getLastChanged();
  const logoUrl = useLogoUrl();

  const inspectorVisibility = getFeatureVisibility("site_inspector");
  const changelogVisibility = getFeatureVisibility("changelog");
  const livesupportVisibility = getFeatureVisibility("livesupport");

  // Rol bazlı + feature visibility filtresi
  const adsVisibility = getFeatureVisibility("ad_manager");

  const navItems = allNavItems.filter((item) => {
    if (item.id === "livesupport") {
      if (livesupportVisibility === "hidden") return false;
    }
    if (item.id === "ads") {
      if (adsVisibility === "hidden") return false;
    }
    if (!item.roles) return true;
    return item.roles.includes(userRole);
  });

  const supportAllowedTabs: Tab[] = ["messages", "livesupport", "settings"];
  const filteredNav = userRole === "support"
    ? navItems.filter(i => supportAllowedTabs.includes(i.id))
    : navItems;

  const isSupport = userRole === "support";

  return (
    <aside className="w-64 bg-[#0F1729] border-r border-[#2B3654] flex flex-col sticky top-0 h-screen overflow-hidden">
      <div className="px-6 py-6 border-b border-[#2B3654] flex-shrink-0">
        <Link to="/" className="flex items-center gap-3 group">
          <img src={logoUrl} alt="Yenigül Yapı" className="h-10 w-auto object-contain" />
        </Link>
        <div className="mt-3">
          <p className="text-[#7B8FAF] text-xs tracking-widest uppercase">Yönetim Paneli</p>
        </div>
      </div>

      <nav className="flex-1 px-3 py-4 flex flex-col gap-0.5 overflow-y-auto">
        {isSupport && (
          <div className="mb-3 mx-1 bg-[#3B82F6]/10 border border-[#3B82F6]/30 rounded-lg px-3 py-2">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-customer-service-2-line text-[#3B82F6] text-sm"></i>
              </div>
              <span className="text-[#A5B5CC] text-xs font-medium">Destek Ekibi</span>
            </div>
            <p className="text-[#465886] text-xs mt-1">Mesajlar ve canlı destek paneline erişim</p>
          </div>
        )}

        {filteredNav.map((item) => {
          const sectionKey = sectionKeyMap[item.id];
          const lastEdit   = sectionKey ? lastChanged[sectionKey] : null;

          // Feature visibility kontrolü
          const featureId  = tabFeatureMap[item.id];
          const visibility = featureId ? getFeatureVisibility(featureId) : "visible";
          const description = featureId ? getFeatureDescription(featureId) : "";

          // Kilitli göster
          if (visibility === "locked") {
            // Eğer bu feature için premium popup config varsa, zengin popup kullan
            if (featureId && PREMIUM_POPUP_CONFIGS[featureId]) {
              return (
                <PremiumLockedButton
                  key={item.id}
                  featureId={featureId}
                  position="right-bottom"
                />
              );
            }
            return (
              <LockedNavButton
                key={item.id}
                label={item.label}
                icon={item.icon}
                description={`Bu özellik devre dışı bırakılmış. ${description}`}
              />
            );
          }

          return (
            <button
              key={item.id}
              onClick={() => onTabChange(item.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer text-left ${
                activeTab === item.id
                  ? "bg-[#3B82F6]/20 text-white border border-[#3B82F6]/40"
                  : "text-[#7B8FAF] hover:text-white hover:bg-white/5 border border-transparent"
              }`}
            >
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className={`${item.icon} text-base`}></i>
              </div>
              <span className="flex-1 truncate">{item.label}</span>
              {item.id === "messages" && newMessages > 0 && (
                <span className="bg-[#3B82F6] text-white text-xs px-1.5 py-0.5 rounded-full font-bold flex-shrink-0">{newMessages}</span>
              )}
              {item.id === "livesupport" && supportUnread > 0 && (
                <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full font-bold flex-shrink-0 animate-pulse">{supportUnread}</span>
              )}
              {item.id === "livesupport" && supportUnread === 0 && visibility === "visible" && (
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse flex-shrink-0"></span>
              )}
              {/* Premium Aktif rozeti */}
              {featureId && isFeatureEnabled(featureId) && (
                <span className="flex-shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-400 text-[9px] font-semibold whitespace-nowrap">
                  <i className="ri-vip-crown-fill text-[8px]"></i>
                  Aktif
                </span>
              )}
              {lastEdit && (
                <span className="text-[#465886] text-xs flex-shrink-0 whitespace-nowrap">{formatRelativeTime(lastEdit)}</span>
              )}
            </button>
          );
        })}

        <div className="h-px bg-[#2B3654] my-2"></div>

        {/* Değişiklik Günlüğü — sadece admin */}
        {userRole === "admin" && (() => {
          if (changelogVisibility === "hidden") return null;
          if (changelogVisibility === "locked") {
            return (
              <PremiumLockedButton
                key="changelog-locked"
                featureId="changelog"
                position="right-bottom"
              />
            );
          }
          return (
            <button
              onClick={() => onTabChange("changelog")}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer text-left ${
                activeTab === "changelog"
                  ? "bg-[#3B82F6]/20 text-white border border-[#3B82F6]/40"
                  : "text-[#7B8FAF] hover:text-white hover:bg-white/5 border border-transparent"
              }`}
            >
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className="ri-history-line text-base"></i>
              </div>
              <span className="flex-1">Değişiklik Günlüğü</span>
              {siteData.getChangeLog().length > 0 && (
                <span className="text-[#465886] text-xs flex-shrink-0">{siteData.getChangeLog().length}</span>
              )}
            </button>
          );
        })()}
      </nav>

      <div className="px-4 py-4 border-t border-[#2B3654] space-y-1.5 flex-shrink-0">
        {/* Görsel İnspektör */}
        {!isSupport && (() => {
          if (inspectorVisibility === "hidden") return null;
          if (inspectorVisibility === "locked") {
            return <InspectorLockedSidebarButton key="inspector-locked" />;
          }
          return (
            <Link
              to="/site-inspector"
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-emerald-500/10 border border-emerald-500/25 text-emerald-300 hover:bg-emerald-500/20 transition-colors cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0"><i className="ri-cursor-line text-sm"></i></div>
              <span className="flex-1 text-xs">Görsel İnspektör</span>
              <span className="flex-shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-400 text-[9px] font-semibold whitespace-nowrap">
                <i className="ri-vip-crown-fill text-[8px]"></i> Aktif
              </span>
            </Link>
          );
        })()}
        <Link to="/" className="flex items-center gap-2 text-[#7B8FAF] hover:text-white text-sm transition-colors cursor-pointer px-2 py-1.5 rounded hover:bg-white/5">
          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0"><i className="ri-external-link-line text-sm"></i></div>
          <span>Siteyi Görüntüle</span>
        </Link>

        {/* Premium Deneme Göstergesi */}
        {isPremiumTrialActive() && (() => {
          const info = getPremiumTrialInfo();
          const hours = Math.floor(info.remainingMs / 3600000);
          const days = Math.floor(hours / 24);
          const label = days > 0 ? `${days}g ${hours % 24}sa` : `${hours}sa`;
          return (
            <div className="mt-1 flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/25">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                <i className="ri-vip-crown-fill text-amber-400 text-sm"></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-amber-300 text-xs font-semibold leading-tight">Premium Deneme</p>
                <p className="text-amber-500/70 text-[10px]">{label} kaldı</p>
              </div>
            </div>
          );
        })()}
      </div>
    </aside>
  );
}
